import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { modeAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from './ConfirmationModal';
import CloseCompanyModal from './CloseCompanyModal';
import CompanyUpdateModal from './CompanyUpdateModal';
import './CompaniesSettings.css';

const CompaniesSettings = () => {
  const navigate = useNavigate();
  const { user, switchMode } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState({});
  const [closeCompanyError, setCloseCompanyError] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const menuRef = useRef(null);
  const [updateModalId, setUpdateModalId] = useState(null);
  const [closeModal, setCloseModal] = useState({ id: null, name: '' });
  const [closePasswordError, setClosePasswordError] = useState(null);
  const [closeLoading, setCloseLoading] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    type: 'close-company',
    id: null,
    title: '',
    message: '',
    meta: null,
  });

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await modeAPI.getCompanyProfiles();
      if (response.success && response.data) {
        setCompanies(Array.isArray(response.data) ? response.data : []);
      } else {
        setCompanies([]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSwitchCompany = async (companyId) => {
    setMenuOpenId(null);
    try {
      const result = await switchMode('company', false, true, companyId);
      if (result.success && result.data?.token) {
        navigate('/profile');
      } else if (!result.success) {
        alert(result.message || 'Failed to switch to company.');
      }
    } catch (err) {
      alert(err?.message || 'Failed to switch to company.');
    }
  };

  const handleUpdate = (company) => {
    setMenuOpenId(null);
    setUpdateModalId(company.id);
  };

  const handleCloseCompanyClick = (company) => {
    setMenuOpenId(null);
    setCloseCompanyError(null);
    const hasActiveJobs = (company.active_jobs_count || 0) > 0;
    if (hasActiveJobs) {
      setConfirmationModal({
        isOpen: true,
        type: 'close-company',
        id: company.id,
        title: 'Close active jobs first',
        message: `This company has ${company.active_jobs_count} active job(s). Close all active posted jobs for this company first, then you can close the company from here.`,
        meta: { active_jobs_count: company.active_jobs_count || 0 },
      });
    } else {
      setCloseModal({ id: company.id, name: company.name });
      setClosePasswordError(null);
    }
  };

  const confirmCloseCompanyFromPassword = async (password) => {
    if (!closeModal.id) return;
    setClosePasswordError(null);
    setCloseLoading(true);
    try {
      const response = await modeAPI.closeCompany(closeModal.id, { password });
      if (response.success) {
        setCloseModal({ id: null, name: '' });
        setCompanies((prev) =>
          prev.map((c) => (c.id === closeModal.id ? { ...c, is_active: false } : c))
        );
      } else {
        setClosePasswordError(response.message || response.errors?.password?.[0] || 'Failed to close company.');
      }
    } catch (err) {
      const data = err?.data || err?.response?.data;
      const msg =
        data?.errors?.password?.[0] ||
        data?.message ||
        err?.message ||
        'Failed to close company.';
      setClosePasswordError(msg);
    } finally {
      setCloseLoading(false);
    }
  };

  const confirmCloseCompany = async () => {
    const id = confirmationModal.id;
    const activeJobsCount = confirmationModal.meta?.active_jobs_count ?? 0;
    setConfirmationModal((prev) => ({ ...prev, isOpen: false }));
    if (activeJobsCount > 0) return;
    setDeleting((prev) => ({ ...prev, [`close-company-${id}`]: true }));
    setCloseCompanyError(null);
    try {
      const response = await modeAPI.closeCompany(id);
      if (response.success) {
        setCompanies((prev) => prev.map((c) => (c.id === id ? { ...c, is_active: false } : c)));
      } else {
        setCloseCompanyError(response.message || 'Failed to close company.');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.data?.message || err?.message || 'Failed to close company.';
      setCloseCompanyError(msg);
    } finally {
      setDeleting((prev) => ({ ...prev, [`close-company-${id}`]: false }));
    }
  };

  const handleUpdateSuccess = (data) => {
    if (data?.id) {
      setCompanies((prev) =>
        prev.map((c) =>
          c.id === data.id
            ? {
                ...c,
                name: data.name ?? c.name,
                address: data.address ?? c.address,
              }
            : c
        )
      );
    }
  };

  const handleOpenCompany = async (company) => {
    setMenuOpenId(null);
    setOpeningId(company.id);
    try {
      const response = await modeAPI.openCompany(company.id);
      if (response.success) {
        setCompanies((prev) =>
          prev.map((c) =>
            c.id === company.id ? { ...c, is_active: true } : c
          )
        );
        alert(response.message || 'Company reopened. You will receive an email and notification.');
      } else {
        alert(response.message || 'Failed to reopen company.');
      }
    } catch (err) {
      alert(err?.message || err?.data?.message || 'Failed to reopen company.');
    } finally {
      setOpeningId(null);
    }
  };

  if (loading) {
    return (
      <div className="companies-settings-container">
        <div className="companies-settings-loading">
          <div className="spinner"></div>
          <p>Loading your companies…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="companies-settings-container">
      <div className="companies-settings-header">
        <h2>My companies</h2>
        <p>Switch to or close companies you created</p>
      </div>

      <div className="companies-settings-section">
        <div className="section-header">
          <h3>Your companies</h3>
          <span className="section-count">{companies.length}</span>
        </div>
        {closeCompanyError && (
          <div className="companies-error-banner">{closeCompanyError}</div>
        )}
        {companies.length === 0 ? (
          <div className="companies-empty-state">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9,22 9,12 15,12 15,22"></polyline>
            </svg>
            <p>No companies yet</p>
            <span>Create a company from Post Jobs to list it here</span>
          </div>
        ) : (
          <div className="companies-list">
            {companies.map((company) => (
              <div key={company.id} className="company-item">
                <div className="company-item-content">
                  <div className="company-item-logo">
                    {company.logo_url ? (
                      <img src={company.logo_url} alt="" />
                    ) : (
                      <span className="company-item-initial">
                        {(company.name || 'C').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="company-item-details">
                    <div className="company-item-header-row">
                      <h4>{company.name}</h4>
                      {company.is_active === false && (
                        <span className="badge closed">Closed</span>
                      )}
                      <span className="badge count">
                        {company.active_jobs_count ?? 0} active job
                        {(company.active_jobs_count ?? 0) === 1 ? '' : 's'}
                      </span>
                    </div>
                    {company.address && (
                      <p className="company-item-address">{company.address}</p>
                    )}
                  </div>
                </div>
                <div className="company-item-actions" ref={menuOpenId === company.id ? menuRef : null}>
                  {user?.id !== company.id && company.is_active !== false && (
                    <button
                      type="button"
                      className="btn-company-action switch"
                      onClick={() => handleSwitchCompany(company.id)}
                      title="Switch to this company"
                    >
                      Switch
                    </button>
                  )}
                  <div className="company-item-menu-wrapper">
                    <button
                      type="button"
                      className="btn-company-action btn-company-menu-trigger"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(menuOpenId === company.id ? null : company.id);
                      }}
                      title="More options"
                      aria-label="More options"
                    >
                      <span className="company-menu-dots">⋯</span>
                    </button>
                    {menuOpenId === company.id && (
                      <div className="company-menu-dropdown">
                        {company.is_active === false ? (
                          <button
                            type="button"
                            className="company-menu-item company-menu-item-open"
                            onClick={() => handleOpenCompany(company)}
                            disabled={openingId === company.id}
                          >
                            {openingId === company.id && <span className="spinner small"></span>}
                            Open back
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="company-menu-item"
                              onClick={() => handleUpdate(company)}
                            >
                              Update
                            </button>
                            <button
                              type="button"
                              className="company-menu-item danger"
                              onClick={() => handleCloseCompanyClick(company)}
                            >
                              Close company
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmCloseCompany}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmText={
          (confirmationModal.meta?.active_jobs_count || 0) > 0 ? 'OK' : 'Close company'
        }
        cancelText="Cancel"
        type={
          (confirmationModal.meta?.active_jobs_count || 0) > 0 ? 'default' : 'danger'
        }
      />

      <CloseCompanyModal
        isOpen={!!closeModal.id}
        onClose={() => setCloseModal({ id: null, name: '' })}
        companyName={closeModal.name}
        onConfirm={confirmCloseCompanyFromPassword}
        loading={closeLoading}
        error={closePasswordError}
      />

      <CompanyUpdateModal
        isOpen={!!updateModalId}
        onClose={() => setUpdateModalId(null)}
        companyId={updateModalId}
        onSuccess={handleUpdateSuccess}
      />
    </div>
  );
};

export default CompaniesSettings;
